import { useWatch } from 'react-hook-form';

import {
	Accordion,
	AccordionBody,
	AccordionHeader,
	Box,
	Form,
	Stack,
	Text,
} from '@ekhodealer/ekho-common/rev';

import type { TagProps } from '@ekhodealer/ekho-common/rev';
import type { ReactNode } from 'react';
import type { z } from 'zod';
import type { AppointmentTypeConfig as AppointmentTypeConfigType } from '../types';

import { CopyableCodeBlock } from '@common/components/CopyableCodeBlock';
import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';

import { useGetAiBdcSettings } from '../api/useGetAiBdcSettings';
import { useUpdateAiBdcSettings } from '../api/useUpdateAiBdcSettings';

import { appointmentTypeSchema } from './appointmentTypeSchema';
import { DurationSelector } from './DurationSelector';

import './SalesAppointmentTypeForm.scss';


export interface AppointmentTypeConfig {
	key: 'testDrive' | 'showroomVisit' | 'salesConsultation' | 'tradeInAppraisal'
		| 'routineMaintenance' | 'diagnosticInspection' | 'recall' | 'bodyWork';
	title: string;
	description: string;
	comingSoon?: boolean;
	defaultEnabled?: boolean;
	tag?: Omit<TagProps, 'children'> & { children: ReactNode };
}

const CALENDAR_PROVIDER_OPTIONS = [
	{ id: 'none', label: 'None' },
	{ id: 'google', label: 'Google' },
];


type AppointmentFormData = z.infer<typeof appointmentTypeSchema>;

function getDefaultValues(config: AppointmentTypeConfigType | undefined, defaultEnabled = false): AppointmentFormData {
	return {
		enabled: config?.enabled ?? defaultEnabled,
		calendarProvider: config?.calendarProvider || 'none',
		calendarId: config?.calendarId ?? '',
		defaultLength: config?.defaultLength ?? 30,
		notificationEmail: config?.notificationEmail ?? '',
	};
}

const SERVICE_ACCOUNT_EMAIL = 'google-calendar-service-accoun@ad-backend-prod.iam.gserviceaccount.com';

function GoogleCalendarInstructions(): React.ReactNode {
	return (
		<Accordion className='install-guide-accordion'>
			<AccordionHeader>
				<Text size='bodySmall' weight='semibold'>
					Connection instructions
				</Text>
			</AccordionHeader>
			<AccordionBody itemsSpacing='16'>
				<Text size='bodySmall' color='secondary'>
					1. Open Google Calendar and go to Settings for the calendar you want to connect.
				</Text>
				<Text size='bodySmall' color='secondary'>
					2. Share the calendar with the Ekho service account so it can check availability and sync bookings.
					Copy the service account email below, add it as a group, and give it &ldquo;Make changes&rdquo; permissions.
				</Text>
				<CopyableCodeBlock label='Service account' value={SERVICE_ACCOUNT_EMAIL} />
				<Text size='bodySmall' color='secondary'>
					3. Scroll to &ldquo;Integrate calendar&rdquo; and copy the Calendar ID
					(looks like <code>name@group.calendar.google.com</code>). Paste it into the field below.
				</Text>
			</AccordionBody>
		</Accordion>
	);
}

/** Fields that respond to the `calendarProvider` selection. Disabled state is handled by the Form toggle. */
function AppointmentFields(): React.ReactNode {
	const calendarProvider = useWatch({ name: 'calendarProvider' }) as string;

	return (
		<Stack itemsSpacing='16'>
			<DurationSelector schemaField='defaultLength' />
			<Form.Field.Text
				schemaField='notificationEmail'
				labelText='Notification email'
				descriptionText='The email address where booking notifications for this appointment type will be sent.'
				type='email'
				size='large'
				placeholder='sales@example.com'
				required
			/>
			<Form.Field.Dropdown
				schemaField='calendarProvider'
				labelText='Calendar provider'
				options={CALENDAR_PROVIDER_OPTIONS}
			/>
			{calendarProvider === 'google' && (
				<>
					<Form.Field.Text
						schemaField='calendarId'
						labelText='Calendar ID'
						descriptionText="The AI will use this calendar to check your team's availability and book appointments directly."
						size='large'
						placeholder='name@group.calendar.google.com'
						required
					/>
					<GoogleCalendarInstructions />
				</>
			)}
		</Stack>
	);
}

const SALES_KEYS = ['testDrive', 'showroomVisit', 'salesConsultation', 'tradeInAppraisal'] as const;
const SERVICE_KEYS = ['routineMaintenance', 'diagnosticInspection', 'recall', 'bodyWork'] as const;

export function SalesAppointmentTypeForm({ config }: { config: AppointmentTypeConfig }) {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);
	const updateMutation = useUpdateAiBdcSettings(spId);

	const isSalesKey = SALES_KEYS.includes(config.key as typeof SALES_KEYS[number]);
	const isServiceKey = SERVICE_KEYS.includes(config.key as typeof SERVICE_KEYS[number]);

	const currentConfig = isSalesKey
		? settings?.skills?.bookings?.sales?.[config.key as typeof SALES_KEYS[number]]
		: isServiceKey
			? settings?.skills?.bookings?.service?.[config.key as typeof SERVICE_KEYS[number]]
			: undefined;

	const handleSubmit = async (data: AppointmentFormData): Promise<{ isSuccess: boolean }> => {
		try {
			const appointmentData = {
				enabled: data.enabled,
				calendarProvider: data.calendarProvider === 'none' ? undefined : data.calendarProvider || undefined,
				calendarId: data.calendarId || undefined,
				defaultLength: data.defaultLength,
				notificationEmail: data.notificationEmail || undefined,
			};

			if (isSalesKey) {
				await updateMutation.mutateAsync({
					skills: {
						...settings?.skills,
						bookings: {
							...settings?.skills?.bookings,
							sales: {
								...settings?.skills?.bookings?.sales,
								[config.key]: appointmentData,
							},
						},
					},
				});
			} else {
				await updateMutation.mutateAsync({
					skills: {
						...settings?.skills,
						bookings: {
							...settings?.skills?.bookings,
							service: {
								...settings?.skills?.bookings?.service,
								[config.key]: appointmentData,
							},
						},
					},
				});
			}
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	if (config.comingSoon) {
		return (
			<Box className='sales-appointment-type-form' padding='24' background='secondary' rounding='12'>
				<div data-settings-disabled>
					<Form
						name={`ai-bdc-sales-appointment-${config.key}`}
						schema={appointmentTypeSchema}
						defaultValues={getDefaultValues(currentConfig, config.defaultEnabled)}
						onSubmit={async () => ({ isSuccess: false })}
						title={config.title}
						titleLevel='subgroup'
						titleWeight='semibold'
						titleMaxWidth='full'
						description={config.description}
						tag={{ children: 'Coming soon', size: 'small' }}
						toggle={{ fieldName: 'enabled' }}>
						<AppointmentFields />
					</Form>
				</div>
			</Box>
		);
	}

	return (
		<Box className='sales-appointment-type-form' padding='24' background='secondary' rounding='12'>
			<Form
				name={`ai-bdc-sales-appointment-${config.key}`}
				schema={appointmentTypeSchema}
				defaultValues={getDefaultValues(currentConfig, config.defaultEnabled)}
				onSubmit={handleSubmit}
				title={config.title}
				titleLevel='subgroup'
				titleWeight='semibold'
				description={config.description}
				tag={config.tag}
				toggle={{ fieldName: 'enabled' }}
				showSaveAndClear>
				<AppointmentFields />
			</Form>
		</Box>
	);
}
